import { useCallback, useState } from "react";
import { withNamespaces } from "react-i18next";
import { Button } from "reactstrap";
import OverviewHeader from "../../../overview-header";
import CreateMeasureModal from "./components/CreateMeasureModal";
import DeleteMeasureModal from "./components/DeleteMeasureModal";
import FilteringComponent from "./components/filtering-component";
import PositiveListAnalysis from "./components/PositiveListAnalysis";
import PositiveListSummary from "./components/PositiveListSummary";
import MeasureDetailOffcanvas from "./components/MeasureDetailOffcanvas";
import PositiveListTable from "./components/PositiveListTable";
import { hasPositiveListFilters } from "./helpers/measureList";
import useDeletePositiveListMeasure from "./hooks/useDeletePositiveListMeasure";
import usePositiveList from "./hooks/usePositiveList";
import useSetPositiveListUsage from "./hooks/useSetPositiveListUsage";

import "./style.scss";

const PositiveList = ({ t, softwareData }) => {
    const [filters, setFilters] = useState({
        usageStatus: null,
        measureType: null,
        search: undefined,
    });
    const [selectedMeasure, setSelectedMeasure] = useState(null);
    const [measureToDelete, setMeasureToDelete] = useState(null);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const handleFilterChange = useCallback((nextFilters) => {
        setFilters(nextFilters);
    }, []);
    const measuresQuery = usePositiveList({
        softwareId: softwareData?.id,
        filters,
        t,
    });
    const setUsageMutation = useSetPositiveListUsage({
        softwareId: softwareData?.id,
        t,
    });
    const handleUsageChange = useCallback((measureId, status) => {
        setUsageMutation.mutate({ measureId, status });
    }, [setUsageMutation]);
    const deleteMutation = useDeletePositiveListMeasure({
        softwareId: softwareData?.id,
        t,
        onSuccess: (deletedId) => {
            setMeasureToDelete(null);
            setSelectedMeasure((current) => (
                current && String(current.id) === String(deletedId) ? null : current
            ));
        },
    });
    const openMeasure = useCallback((measure) => {
        setSelectedMeasure(measure);
    }, []);
    const summary = measuresQuery.data?.summary;
    const items = measuresQuery.data?.items || [];

    return (
        <div className="positive-list p-4">
            <div className="mb-4">
                <OverviewHeader assetId={softwareData.id} />
            </div>

            <div className="mb-3">
                <h4 className="mb-1">{t("Positive List")}</h4>
                <p className="text-muted mb-0">
                    {t(
                        "Security, technical, organizational, and operational measures for this software."
                    )}
                </p>
            </div>

            <PositiveListAnalysis softwareId={softwareData?.id} t={t} />

            {measuresQuery.isError && (
                <div className="text-danger py-3">
                    {t("An error occurred while fetching implemented measures.")}
                </div>
            )}

            {summary && (
                <PositiveListSummary summary={summary} t={t} />
            )}

            <div className="positive-list__toolbar">
                <FilteringComponent onChange={handleFilterChange} />
                <Button
                    color="primary"
                    className="flex-shrink-0"
                    onClick={() => setIsCreateOpen(true)}
                >
                    {t("Add measure")}
                </Button>
            </div>

            <PositiveListTable
                items={items}
                loading={measuresQuery.isFetching || measuresQuery.isLoading}
                isFetched={measuresQuery.isFetched}
                filtersAreActive={hasPositiveListFilters(filters)}
                pendingMeasureId={
                    setUsageMutation.isLoading
                        ? setUsageMutation.variables?.measureId
                        : null
                }
                deletingMeasureId={
                    deleteMutation.isLoading ? deleteMutation.variables : null
                }
                onUsageChange={handleUsageChange}
                onViewMeasure={openMeasure}
                onDeleteMeasure={setMeasureToDelete}
                t={t}
            />

            <MeasureDetailOffcanvas
                softwareId={softwareData?.id}
                isOpen={Boolean(selectedMeasure)}
                measureId={selectedMeasure?.id}
                listTitle={selectedMeasure?.title}
                onClose={() => setSelectedMeasure(null)}
                t={t}
            />

            <DeleteMeasureModal
                isOpen={Boolean(measureToDelete)}
                isDeleting={deleteMutation.isLoading}
                onCancel={() => setMeasureToDelete(null)}
                onConfirm={() => deleteMutation.mutate(measureToDelete.id)}
                t={t}
            />

            <CreateMeasureModal
                isOpen={isCreateOpen}
                softwareId={softwareData?.id}
                onClose={() => setIsCreateOpen(false)}
                onCreated={openMeasure}
                t={t}
            />
        </div>
    );
};

export default withNamespaces()(PositiveList);
